import React from "react";
import { useForm, Controller } from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input/mobile";
import validator from "validator";

import { ReactComponent as AlertIcon } from "../../assets/images/input-error.svg";

/**
 * The inline guest form for one seat.
 *
 * Gender values are the API's own single letters, "m" and "f" — not
 * "male"/"female", which the backend column cannot hold.
 *
 * Email is required unconditionally -- NOT gated on the event's
 * `requireEmailForTickets`. resolve_guest_customer identifies a guest by email
 * alone (phone is stored but never used for lookup), so
 * TableSeatAssignSerializer declares a plain required EmailField and a form that
 * let it through earned a 400.
 *
 * Field appearance is inherited from `.registration-form__input`, the class the
 * rest of the app's forms use — it also owns the dark-mode autofill overrides,
 * which a bespoke input class would silently lose.
 */
const GENDERS = [
  { value: "m", label: "Male" },
  { value: "f", label: "Female" },
];

const TableSeatForm = ({
  seat,
  onSubmit,
  onCancel,
  submitting,
  errorMessage,
}) => {
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    // Errors on blur, not on every keystroke: a half-typed name is not a
    // mistake yet.
    mode: "onTouched",
    defaultValues: {
      firstName: seat.firstName || "",
      lastName: seat.lastName || "",
      mobileNumber: seat.mobileNumber || "",
      gender: seat.gender || "",
      email: seat.email || "",
    },
  });

  const gender = watch("gender");
  // Two seats can be open for editing at once, so every id is seat-scoped.
  const fieldId = (name) => `seat-${seat.position}-${name}`;
  const errorId = (name) => `${fieldId(name)}-error`;
  const describedBy = (name, helper) =>
    [
      errors[name] ? errorId(name) : null,
      helper ? `${fieldId(name)}-help` : null,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

  const inputClass = (name) =>
    `registration-form__input table-seat-form__input${
      errors[name] ? " table-seat-form__input--error" : ""
    }`;

  return (
    <form
      className="table-seat-form"
      onSubmit={handleSubmit(onSubmit)}
      // Validation is ours, so the browser's own bubbles never appear next to
      // our inline messages.
      noValidate
    >
      {errorMessage ? (
        <div className="table-seat-form__error" role="alert">
          <AlertIcon aria-hidden="true" />
          {errorMessage}
        </div>
      ) : null}

      <div className="table-seat-form__grid">
        <div className="table-seat-form__field">
          <label
            className="table-seat-form__label"
            htmlFor={fieldId("firstName")}
          >
            First name *
          </label>
          <input
            id={fieldId("firstName")}
            className={inputClass("firstName")}
            autoComplete="given-name"
            aria-invalid={errors.firstName ? "true" : "false"}
            aria-describedby={describedBy("firstName")}
            {...register("firstName", { required: "First name is required" })}
          />
          {errors.firstName ? (
            <span
              className="table-seat-form__field-error"
              id={errorId("firstName")}
              role="alert"
            >
              {errors.firstName.message}
            </span>
          ) : null}
        </div>

        <div className="table-seat-form__field">
          <label
            className="table-seat-form__label"
            htmlFor={fieldId("lastName")}
          >
            Last name
          </label>
          <input
            id={fieldId("lastName")}
            className={inputClass("lastName")}
            autoComplete="family-name"
            {...register("lastName")}
          />
        </div>

        <div className="table-seat-form__field">
          <label
            className="table-seat-form__label"
            htmlFor={fieldId("mobileNumber")}
          >
            Mobile number *
          </label>
          <Controller
            control={control}
            name="mobileNumber"
            rules={{
              required: "Mobile number is required",
              validate: (value) =>
                isValidPhoneNumber(value || "") ||
                "That mobile number doesn't look right.",
            }}
            render={({ field }) => (
              <PhoneInput
                id={fieldId("mobileNumber")}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                defaultCountry="EG"
                className={errors.mobileNumber ? "PhoneInput--error" : ""}
                aria-describedby={describedBy("mobileNumber", true)}
              />
            )}
          />
          <span
            className="table-seat-form__help"
            id={`${fieldId("mobileNumber")}-help`}
          >
            Used to identify the guest at the door.
          </span>
          {errors.mobileNumber ? (
            <span
              className="table-seat-form__field-error"
              id={errorId("mobileNumber")}
              role="alert"
            >
              {errors.mobileNumber.message}
            </span>
          ) : null}
        </div>

        {/* Two options, so a segmented control: one tap, and no OS-native
            select rendering light-on-dark. */}
        <fieldset className="table-seat-form__field table-seat-form__fieldset">
          <legend className="table-seat-form__label" id={fieldId("gender")}>
            Gender *
          </legend>
          {/* The invalid state belongs to the group, not to each radio. */}
          <div
            className="table-seat-form__segments"
            role="radiogroup"
            aria-labelledby={fieldId("gender")}
            aria-invalid={errors.gender ? "true" : "false"}
            aria-describedby={describedBy("gender")}
          >
            {GENDERS.map((option) => (
              <label
                key={option.value}
                className={`table-seat-form__segment${
                  gender === option.value
                    ? " table-seat-form__segment--selected"
                    : ""
                }${errors.gender ? " table-seat-form__segment--error" : ""}`}
              >
                <input
                  type="radio"
                  value={option.value}
                  aria-invalid={errors.gender ? "true" : "false"}
                  {...register("gender", { required: "Gender is required" })}
                />
                {option.label}
              </label>
            ))}
          </div>
          {errors.gender ? (
            <span
              className="table-seat-form__field-error"
              id={errorId("gender")}
              role="alert"
            >
              {errors.gender.message}
            </span>
          ) : null}
        </fieldset>

        <div className="table-seat-form__field table-seat-form__field--full">
          {/* Always required, whatever the event's own email policy says:
              resolve_guest_customer identifies a guest by email ALONE (phone is
              stored but never used for lookup), so TableSeatAssignSerializer
              declares a plain required EmailField. Marking it optional here
              earned a 400. */}
          <label className="table-seat-form__label" htmlFor={fieldId("email")}>
            Email *
          </label>
          <input
            id={fieldId("email")}
            type="email"
            inputMode="email"
            autoComplete="email"
            className={inputClass("email")}
            aria-invalid={errors.email ? "true" : "false"}
            aria-describedby={describedBy("email", true)}
            {...register("email", {
              required: "Email is required",
              validate: (value) =>
                validator.isEmail(value || "") ||
                "That email address doesn't look right.",
            })}
          />
          <span
            className="table-seat-form__help"
            id={`${fieldId("email")}-help`}
          >
            Their pass is emailed here, and it identifies them at the door.
          </span>
          {errors.email ? (
            <span
              className="table-seat-form__field-error"
              id={errorId("email")}
              role="alert"
            >
              {errors.email.message}
            </span>
          ) : null}
        </div>
      </div>

      <div className="table-seat-form__actions">
        <button
          type="submit"
          className="table-seat-form__submit"
          disabled={submitting}
        >
          {submitting ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          className="table-seat-form__cancel"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default TableSeatForm;
